import supabase from '../supabase/supabase';

const TABLES = {
  courses: 'hms_academy_courses_1715725000000',
  versions: 'hms_academy_course_versions_1715725000000',
  learners: 'hms_academy_learners_1715725000000',
  enrollments: 'hms_academy_enrollments_1715726000000',
  completions: 'hms_academy_completions_1715727000000',
  certificates: 'hms_academy_certificates_1715729000000'
};

const FIELDS = {
  courses: 'id,title,status',
  versions: 'id,course_id,version,title,status',
  learners: 'id,first_name,last_name,status',
  enrollments: 'id,learner_id,course_version_id,status,created_at',
  completions: 'id,enrollment_id,completed_at',
  certificates: 'id,completion_id,certificate_number,issued_at,status'
};

const PAGE_SIZE = 500;

function throwReadError(label, error) {
  throw new Error(`Kunne ikke lese ${label}: ${error.message}`);
}

async function readAllRows(table, fields, label) {
  const rows = [];
  let offset = 0;

  while (true) {
    const { data, error } = await supabase
      .from(table)
      .select(fields)
      .order('id', { ascending: true })
      .range(offset, offset + PAGE_SIZE - 1);

    if (error) throwReadError(label, error);

    const page = data || [];
    rows.push(...page);

    if (page.length < PAGE_SIZE) return rows;
    offset += PAGE_SIZE;
  }
}

function createActivity(type, date, enrollment, versionsById, coursesById, learnersById) {
  const version = versionsById.get(enrollment?.course_version_id);
  const learner = learnersById.get(enrollment?.learner_id);
  const course = version && coursesById.get(version.course_id);

  return {
    type,
    date,
    course: course?.title || version?.title || 'Ukjent kurs',
    learner: learner
      ? `${learner.first_name} ${learner.last_name}`.trim()
      : 'Deltaker ikke tilgjengelig'
  };
}

export async function getAcademyAdminDashboard() {
  const [courses, versions, learners, enrollments, completions, certificates] =
    await Promise.all([
      readAllRows(TABLES.courses, FIELDS.courses, 'Academy-kurs'),
      readAllRows(TABLES.versions, FIELDS.versions, 'Academy-kursversjoner'),
      readAllRows(TABLES.learners, FIELDS.learners, 'Academy-deltakere'),
      readAllRows(TABLES.enrollments, FIELDS.enrollments, 'Academy-påmeldinger'),
      readAllRows(TABLES.completions, FIELDS.completions, 'Academy-fullføringer'),
      readAllRows(TABLES.certificates, FIELDS.certificates, 'Academy-kursbevis')
    ]);

  const coursesById = new Map(courses.map((course) => [course.id, course]));
  const versionsById = new Map(versions.map((version) => [version.id, version]));
  const learnersById = new Map(learners.map((learner) => [learner.id, learner]));
  const enrollmentsById = new Map(
    enrollments.map((enrollment) => [enrollment.id, enrollment])
  );

  const completionsByEnrollment = new Map();
  completions.forEach((completion) => {
    const enrollment = enrollmentsById.get(completion.enrollment_id);
    if (!enrollment) return;

    const count = completionsByEnrollment.get(enrollment.course_version_id) || 0;
    completionsByEnrollment.set(enrollment.course_version_id, count + 1);
  });

  const certificatesByVersion = new Map();
  certificates
    .filter((certificate) => certificate.status === 'issued')
    .forEach((certificate) => {
      const completion = completions.find(
        (row) => row.id === certificate.completion_id
      );
      const enrollment = completion && enrollmentsById.get(completion.enrollment_id);
      if (!enrollment) return;

      const count = certificatesByVersion.get(enrollment.course_version_id) || 0;
      certificatesByVersion.set(enrollment.course_version_id, count + 1);
    });

  const enrollmentsByVersion = new Map();
  enrollments
    .filter((enrollment) =>
      ['pending', 'active', 'completed'].includes(enrollment.status)
    )
    .forEach((enrollment) => {
      const count = enrollmentsByVersion.get(enrollment.course_version_id) || 0;
      enrollmentsByVersion.set(enrollment.course_version_id, count + 1);
    });

  const courseRows = versions
    .map((version) => {
      const course = coursesById.get(version.course_id);
      if (!course) return null;

      return {
        id: version.id,
        name: course.title,
        version: version.version,
        status: version.status,
        enrollmentCount: enrollmentsByVersion.get(version.id) || 0,
        completionCount: completionsByEnrollment.get(version.id) || 0,
        certificateCount: certificatesByVersion.get(version.id) || 0
      };
    })
    .filter(Boolean)
    .sort((first, second) => first.name.localeCompare(second.name, 'nb'));

  const activity = [];

  enrollments.forEach((enrollment) => {
    if (enrollment.created_at) {
      activity.push(createActivity(
        'Påmelding',
        enrollment.created_at,
        enrollment,
        versionsById,
        coursesById,
        learnersById
      ));
    }
  });

  completions.forEach((completion) => {
    const enrollment = enrollmentsById.get(completion.enrollment_id);
    if (enrollment && completion.completed_at) {
      activity.push(createActivity(
        'Fullføring',
        completion.completed_at,
        enrollment,
        versionsById,
        coursesById,
        learnersById
      ));
    }
  });

  certificates
    .filter((certificate) => certificate.status === 'issued')
    .forEach((certificate) => {
      const completion = completions.find(
        (row) => row.id === certificate.completion_id
      );
      const enrollment = completion && enrollmentsById.get(completion.enrollment_id);
      if (enrollment && certificate.issued_at) {
        activity.push(createActivity(
          'Kursbevis utstedt',
          certificate.issued_at,
          enrollment,
          versionsById,
          coursesById,
          learnersById
        ));
      }
    });

  activity.sort((first, second) => new Date(second.date) - new Date(first.date));

  return {
    metrics: {
      publishedCourses: new Set(
        versions
          .filter((version) =>
            version.status === 'approved' &&
            coursesById.get(version.course_id)?.status === 'approved'
          )
          .map((version) => version.course_id)
      ).size,
      participants: new Set(
        enrollments
          .filter((enrollment) =>
            ['pending', 'active', 'completed'].includes(enrollment.status)
          )
          .map((enrollment) => enrollment.learner_id)
      ).size,
      activeEnrollments: enrollments.filter(
        (enrollment) => enrollment.status === 'active'
      ).length,
      completions: completions.length,
      issuedCertificates: certificates.filter(
        (certificate) => certificate.status === 'issued'
      ).length,
      ongoingCourses: new Set(
        enrollments
          .filter((enrollment) => enrollment.status === 'active')
          .map((enrollment) => enrollment.course_version_id)
      ).size
    },
    courses: courseRows,
    activity: activity.slice(0, 12)
  };
}